import { fuseAnimations } from '@fuse/animations';
import { MatDialog } from '@angular/material/dialog';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { PrintService } from 'app/shared/services/print.service';
import { BaseComponent } from '../../base/base.component';
import { Subscription } from 'rxjs';
import {
  enumGroupReportReturnType,
  enumReportType,
  Report,
  ReportGroupData,
  ReportListData,
} from 'app/shared/models/report';
import { DataGridComponent } from 'app/components/data-grid/data-grid.component';
import { ReportDialogComponent } from '../report-dialog/report-dialog.component';
import { AuthService } from 'app/shared/services/auth.service';
import { SubjectType } from 'app/shared/models/subject-type';
import { ProcessType } from 'app/shared/models/process-type';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { ProcessTypeService } from 'app/shared/services/process-type.service';
import { ReportHelper } from 'app/shared/services/reporting/report-helper';
import { IReportService } from 'app/shared/services/reporting/base-report.service';
import { EventService } from 'app/shared/services/event.service';
import { DataField } from 'app/shared/models/data-field';
import { DateHelper } from '../../misc/date.module';
import { DxChartComponent, DxPieChartComponent } from 'devextreme-angular';
import { UserService } from 'app/shared/services/user.service';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  animations: fuseAnimations,
})
export class ReportComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('content') content: ElementRef;
  @ViewChild('viewMenuTrigger') viewMenuTrigger: any;
  @ViewChild('chartTypeMenuTrigger') chartTypeMenuTrigger: any;
  @ViewChild(DataGridComponent) dataGrid: DataGridComponent;
  // @ViewChild(DataGrid2Component) dataGrid: DataGrid2Component;

  @ViewChild(DxPieChartComponent) pieChart: DxPieChartComponent;
  @ViewChild(DxChartComponent) barChart: DxChartComponent;

  onDataChanged: Subscription;
  noData = true;

  chartData: ReportGroupData[];
  chartType = 'Pie';
  title = 'Chart Title';
  pieType = 'Pie';
  doughnut = false;
  view: any[] = [window.innerWidth / 2, window.innerHeight / 2];

  hasValue = false;
  loadingVisible: boolean = false;

  report: Report;
  showChart = true;
  showData = true;
  showFilter = false;
  showViewMenu = true;
  userReadOnly = false;
  chartFilterApplied = false;
  currentGridFilter: any;
  userCanExportData = false;

  subjectType: SubjectType = null;
  processType: ProcessType = null;

  onReportChanged$: Subscription;
  onReportDataChanged$: Subscription;
  onReportFilterChanged$: Subscription;
  onGenerateReportStarted$: Subscription;
  onGenerateReportCompleted$: Subscription;

  reportService: IReportService;

  constructor(
    public dialog: MatDialog,
    private reportHelper: ReportHelper,
    private printService: PrintService,
    private authService: AuthService,
    private subjectTypeService: SubjectTypeService,
    private processTypeService: ProcessTypeService,
    private eventService: EventService,
    private fb: UntypedFormBuilder,
    private dateHelper: DateHelper,
    private userService: UserService,
  ) {
    super();
    const user = this.userService.getCurrentUser();
    if (user.canExportData) {
      this.userCanExportData = true;
    }
  }

  // grid stuff
  listData: ReportListData[];
  columns = [];

  ngOnInit() {
    this.onGenerateReportStarted$ =
      this.eventService.onGenerateReportStarted.subscribe((report) => {
        this.loadingVisible = true;
      });
    this.onGenerateReportCompleted$ =
      this.eventService.onGenerateReportCompleted.subscribe((report) => {
        this.loadingVisible = false;
      });

    this.onReportChanged$ = this.eventService.onReportItemChanged.subscribe(
      (report) => {
        this.reportService = this.reportHelper.getReportService(report);
        this.report = report;
        this.chartType = this.report.config.chartType || 'Pie';
        this.title = this.report.description;
        this.setChartType(this.chartType);

        if (report.config.showChart != undefined)
          this.showChart = report.config.showChart;
        else this.showChart = report.config.options ? false : true;

        if (report.config.showData != undefined)
          this.showData = report.config.showData;
        else this.showData = report.config.options ? false : true;

        if (this.showChart === true && this.showData === true)
          this.showViewMenu = true;
        else this.showViewMenu = false;
      }
    );

    this.onReportDataChanged$ = this.eventService.onReportDataChanged.subscribe(
      (data) => {
        if (data) {
          this.chartFilterApplied = false;
          this.subjectType = this.subjectTypeService.getByIdFromCache(
            data.subjectTypeId
          );
          this.processType = this.processTypeService.getByIdFromCache(
            data.processTypeId
          );
          if (data.reportData.listData && data.reportData.listData.length > 0) {
            var listData = JSON.parse(data.reportData.listData);
            if (listData.length > 0) {
              this.noData = false;

              setTimeout(() => {
                this.setGridData(listData);
              }, 200);

              setTimeout(() => {
                if (!this.chartFilterApplied) {
                  this.setChartData(this.listData);
                }
              }, 200);
            } else {
              this.noData = true;
            }
          } else {
            this.noData = true;
          }
        }
      }
    );

    this.userReadOnly = this.authService.isUserReadOnly();
  }

  sortLegendItems(items) {
    items.forEach((item) => {
      // capitalize first letter of each word
      // item.text = item.text.split(' ').map(word => word[0].toUpperCase() + word.substr(1).toLowerCase()).join(' ');

      // add percentage
      const num = item.points.length > 0 ? item.points[0].percent * 100 : 0;
      const percentage = num > 0 ? Math.round(num * 10 ** 2) / 10 ** 2 : 0;
      item.text = item.text + `  (${percentage}%)`;
    });

    // sort results and return
    return items.sort((a, b) => {
      let itemA = a.text.toLowerCase();
      let itemB = b.text.toLowerCase();
      if (itemA < itemB) return -1;
      if (itemA > itemB) return 1;
      return 0;
    });
  }

  ngOnDestroy() {
    if (this.onReportChanged$) {
      this.onReportChanged$.unsubscribe();
    }
    if (this.onReportDataChanged$) {
      this.onReportDataChanged$.unsubscribe();
    }
    if (this.onReportFilterChanged$) {
      this.onReportFilterChanged$.unsubscribe();
    }
    if (this.onGenerateReportCompleted$) {
      this.onGenerateReportCompleted$.unsubscribe();
    }
    if (this.onGenerateReportStarted$) {
      this.onGenerateReportStarted$.unsubscribe();
    }
  }

  pieChartTooltip(arg: any) {
    return {
      text: `${arg.argumentText}<br/>(${(arg.percent * 100.0).toFixed(
        2
      )}%) (${arg.value.toFixed(2)})`,
    };
  }

  barChartTooltip(arg: any) {
    // Note. The bar chart control uses multiple series so that each bar is a separate color. This means
    // that the percentage can not be caclulated over all the series of data.
    return {
      text: `${arg.argumentText}<br/> (${arg.value.toFixed(2)})`,
    };
  }

  onPointHoverChanged(e) {
    const point = e.target;
    if (point.isHovered()) point.showTooltip();
  }

  onPointClick(e) {
    const point = e.target;
    point.showTooltip();
  }

  setChartData(data) {
    if (data && this.report && this.report.config) {
      // set property to group by
      let prop = '';
      switch (this.report.config.reportType) {
        case enumReportType.ConcernsByChannelSource:
          prop = 'ChannelSource';
          break;
        case enumReportType.ConcernsByStatus:
          prop = 'Status';
          break;
        case enumReportType.ConcernsByType:
          prop = 'CaseType';
          break;
        case enumReportType.WhistleblowerReportSource:
          prop = 'WhistleblowerSource';
          break;

        case enumReportType.ReportFindingsPerEmployee:
          prop = '';
          break;
        case enumReportType.AnonymousFollowUp:
          break;
        case enumReportType.IncidentvsReportDate:
          break;

        case enumReportType.ConfidentialityBreakdown:
          prop = 'ConfidentialityBreakdown';
          break;
        case enumReportType.SatisfactionOutcome:
          prop = 'OutcomeType';
          break;

        default:
          if (this.report.config.filterDataType === 'tasktypes') {
            prop = 'TaskName';
          } else {
            // if groupby field is category then group by CategoryId, else use GroupByValue
            const dataField = this.getDataFieldById(this.report.config.groupBy);
            prop =
              dataField && dataField.dataType.toLowerCase() === 'category'
                ? 'CategoryId'
                : 'GroupByValue';
          }
          break;
      }

      // group data
      const groupedMap = data.reduce(
        (entryMap, e) =>
          entryMap.set(e[prop], [...(entryMap.get(e[prop]) || []), e]),
        new Map()
      );
      const dt = Array.from(groupedMap).map((i: any) => {
        // if value by xxx report we need to return the sum of the value
        if (this.report.config.aggregate === 'sum') {
          let AggregateValue = 0.0;
          i[1].forEach((j) => {
            AggregateValue =
              AggregateValue + Number.parseFloat(j.AggregateValue);
          });
          return { name: i[0] || 'Blank', value: AggregateValue || 0 };
        } else {
          return { name: i[0] || 'Blank', value: i[1].length || 0 };
        }
      });

      // Sort data and apply to chart
      this.chartData = [...dt.sort((a, b) => a.name.localeCompare(b.name))];
    }
  }

  applyFilterToChart(filter) {
    this.chartFilterApplied = true;
    const predicate = this.getFilterAsPredicate(filter);
    // console.log('Data and filter', this.listData, filter, predicate);
    const data: any[] = filter
      ? [...this.listData.filter((x) => this.getFilterAsPredicate(filter)(x))]
      : [...this.listData];
    this.setChartData(data);
  }

  private getFilterAsPredicate(filter): (x: any) => boolean {
    // console.log('Filter', filter);
    if (filter) {
      const isArray = Array.isArray(filter[0]);
      let predicate = this.getPredicate(isArray ? filter[0] : filter);
      if (isArray) {
        let op = 'and';
        filter.forEach((i) => {
          switch (i) {
            case 'and':
              op = 'and';
              break;
            case 'or':
              op = 'or';
              break;
            default:
              const priorPredicate = predicate;
              if (op === 'or') {
                predicate = (x) => priorPredicate(x) || this.getPredicate(i)(x);
              } else {
                predicate = (x) => priorPredicate(x) && this.getPredicate(i)(x);
              }
          }
        });
      }
      return predicate;
    }
  }

  private getPredicate(filter): (x: any) => boolean {
    if (filter && Array.isArray(filter)) {
      if (filter.length > 3 || Array.isArray(filter[0])) {
        let predicate = this.getPredicate(filter[0]);
        let op = 'and';
        filter.forEach((i) => {
          switch (i) {
            case 'and':
              op = 'and';
              break;
            case 'or':
              op = 'or';
              break;
            default:
              const priorPredicate = predicate;
              if (op === 'or') {
                predicate = (x) => priorPredicate(x) || this.getPredicate(i)(x);
              } else {
                predicate = (x) => priorPredicate(x) && this.getPredicate(i)(x);
              }
          }
        });
        return predicate;
      }
      // console.log('Applying filter', filter);
      const f = filter[0];
      const v = (filter[2] || '').toString().toLowerCase();
      try {
        switch (filter[1]) {
          case '>=':
            return (x) => (x[f] || 0) >= filter[2];
          case '>':
            return (x) => (x[f] || 0) > filter[2];
          case '<':
            return (x) => (x[f] || 0) < filter[2];
          case '=':
            return (x) => (x[f] || '').toString().toLowerCase() === v;
          case '<>':
            return (x) => (x[f] || '').toString().toLowerCase() !== v;
          case 'endswith':
            return (x) => (x[f] || '').toString().toLowerCase().endsWith(v);
          case 'notcontains':
            return (x) => !(x[f] || '').toString().toLowerCase().includes(v);
          case 'startswith':
            return (x) => (x[f] || '').toString().toLowerCase().startsWith(v);
          default:
            return (x) => (x[f] || '').toString().toLowerCase().includes(v);
        }
      } catch {}
    }
    return (x) => x;
  }

  setGridData(data) {
    if (data) {
      const reportData = data;
      if (reportData.length > 0) {
        switch (this.report.config.reportType) {
          case enumReportType.ReportFindingsPerEmployee:
          case enumReportType.AnonymousFollowUp:
          case enumReportType.IncidentvsReportDate:
            this.setClientAABReportColumns(data[0]);
            break;

          case enumReportType.ConcernsByChannelSource:
          case enumReportType.ConcernsByStatus:
          case enumReportType.ConcernsByType:
          case enumReportType.WhistleblowerReportSource:
          case enumReportType.ConfidentialityBreakdown:
          case enumReportType.SatisfactionOutcome:
            // set columns for client referral report
            this.setClientReferralColumns(
              reportData[0].hasOwnProperty('Archived'),
              data[0]
            );
            break;
          default:
            // by default we assume must be a system report and process accordingly
            if (this.report.config.filterDataType === 'tasktypes') {
              this.setTaskColumns(
                reportData[0].hasOwnProperty('Archived'),
                reportData[0].hasOwnProperty('IsReferral'),
                data[0]
              );
            } else {
              this.setColumns(
                reportData[0].hasOwnProperty('Archived'),
                reportData[0].hasOwnProperty('DateIncurred'),
                reportData[0].hasOwnProperty('IsReferral'),
                reportData[0]
              );
            }
            break;
        }

        if (this.showData) {
          setTimeout(() => {
            // setup event listener so any changes to state/filters of grid updates chart
            this.onReportFilterChanged$ =
              this.dataGrid.dataGrid.onContentReady.subscribe((_) => {
                setTimeout(() => {
                  // devx grid doesn't update properly unless we give it a chance to update first
                  // doesn't need a long delay - a few milliseconds should be enough...
                  const filter =
                    this.dataGrid.dataGrid.instance.getCombinedFilter(true);
                  if (filter !== this.currentGridFilter) {
                    this.currentGridFilter = filter;
                    this.applyFilterToChart(filter);
                  }
                }, 500); // timeout value was 10ms, which is a bit fast
              });

            if (this.report && this.report.config && this.report.config.state) {
              const state = JSON.parse(this.report.config.state);
              this.dataGrid.instance.state(state);

              setTimeout(() => {
                // devx grid doesn't update properly unless we give it a chance to update first
                // doesn't need a long delay - a few milliseconds should be enough...
                this.showFilter = state.filterPanel.filterEnabled;
                this.dataGrid.setFilterVisibility(this.showFilter);
              }, 10);
            }
          });
        }

        // get any columns with a data type of 'date'
        const dataFieldsWithDate: DataField[] = [];
        if (this.reportService.subjectType) {
          dataFieldsWithDate.push(
            ...this.reportService.subjectType.dataFields.filter(
              (i) => i.dataType.toLowerCase() === 'date'
            )
          );
        }
        if (this.processType) {
          dataFieldsWithDate.push(
            ...this.processType.dataFields.filter(
              (i) => i.dataType.toLowerCase() === 'date'
            )
          );
        }

        const fieldsToModify = [];
        Object.keys(reportData[0]).forEach((e) => {
          const field = dataFieldsWithDate.find(
            (i) => i.name.toLowerCase() === e.toLowerCase()
          );
          if (field) {
            fieldsToModify.push(e);
          }
        });

        // modify the identified columns in the data
        const dd = this.dateHelper.utcDate('01/01/1900');
        reportData.forEach((row) => {
          fieldsToModify.forEach((item) => {
            if (row[item]) {
              try {
                const d = this.dateHelper.utcDate(row[item]);
                row[item] =
                  d > dd ? this.dateHelper.utcDate(row[item]) : undefined;
              } catch {
                row[item] = undefined;
              }
            }
          });
        });

        // update with the incoming data
        this.listData = [...reportData];
      } else {
        this.listData = [];
        this.noData = true;
      }
    }
  }

  setClientAABReportColumns(data: any) {
    const columns: any[] = [];
    switch (this.report.config.reportType) {
      case enumReportType.ReportFindingsPerEmployee:
        columns.push({ name: 'Organisation', prop: 'OrganisationName' });
        columns.push({ name: 'Referrals', prop: 'ReferralCount' });
        columns.push({ name: 'Number of Staff', prop: 'NumberOfStaff' });
        columns.push({
          name: 'Findings Per Employee',
          prop: 'FindingsPerEmployee',
        });
        break;

      case enumReportType.AnonymousFollowUp:
        columns.push({ name: 'Organisation', prop: 'OrganisationName' });
        columns.push({
          name: '% of anonymous view report',
          prop: 'AvgMetric1',
        });
        columns.push({
          name: '% of anonymous update report',
          prop: 'AvgMetric2',
        });
        break;

      case enumReportType.IncidentvsReportDate:
        columns.push({ name: 'Organisation', prop: 'OrganisationName' });
        columns.push({ name: 'Difference', prop: 'Difference' });
        break;
    }

    this.dataGrid.instance.beginUpdate();
    this.columns = [...columns];
    // this.columns=this.columns.filter(c=> data.hasOwnProperty(c.prop));
    this.columns.map((x, idx) => (x.id = idx));
    this.dataGrid.instance.endUpdate();
  }

  setClientReferralColumns(includeArchived: boolean, data: any) {
    const columns: any[] = [];

    columns.push({ name: 'Case Reference', prop: 'Reference', width: 150 });
    // columns.push({name: 'Summary', prop: 'Summary'});

    // TODO:  Include Organisation if has permission to see Organisation filter
    columns.push({ name: 'Organisation', prop: 'Organisation' });

    columns.push({ name: 'Case Category', prop: 'Category' });
    columns.push({ name: 'Case Type', prop: 'CaseType' });

    switch (this.report.config.reportType) {
      case enumReportType.ConcernsByChannelSource:
        columns.push({ name: 'Channel Source', prop: 'ChannelSource' });
        break;
      case enumReportType.ConcernsByStatus:
        break;
      case enumReportType.ConcernsByType:
        break;

      case enumReportType.WhistleblowerReportSource:
        columns.push({
          name: 'Whistleblower Source',
          prop: 'WhistleblowerSource',
        });
        break;
      case enumReportType.ConfidentialityBreakdown:
        columns.push({
          name: 'Confidentiality Breakdown',
          prop: 'ConfidentialityBreakdown',
          width: 160,
        });
        break;
      case enumReportType.SatisfactionOutcome:
        break;
      default:
        break;
    }

    columns.push({ name: 'Case Status', prop: 'Status' }); // subject status
    columns.push({ name: 'Nominated User', prop: 'UserName' });
    columns.push({
      name: 'Date Referred',
      prop: 'DateReferred',
      dataType: 'date',
    });

    columns.push({
      name: 'Accepted Date',
      prop: 'AcceptedDate',
      dataType: 'date',
    });
    columns.push({
      name: 'Date of Closure',
      prop: 'DateClosed',
      dataType: 'date',
    });

    columns.push({
      name: 'Satisfaction Outcome',
      prop: 'OutcomeType',
    });

    if (includeArchived) {
      columns.push({ name: 'Archived', prop: 'Archived', dataType: 'boolean' });
    }

    this.dataGrid.instance.beginUpdate();
    this.columns = [...columns];
    // this.columns=this.columns.filter(c=> data.hasOwnProperty(c.prop));
    this.columns.map((x, idx) => (x.id = idx));
    this.dataGrid.instance.endUpdate();
  }

  setTaskColumns(includeArchived: boolean, isReferral: boolean, data: any) {
    const taskColumns: any[] = [];

    if (this.report.config.filterDataType === 'tasktypes') {
      taskColumns.push({
        name: 'Subject Reference',
        prop: 'Reference',
        width: 150,
      });
      taskColumns.push({
        name: 'Date Created',
        prop: 'DateTaskCreated',
        dataType: 'date',
      });
      taskColumns.push({
        name: 'Date Due',
        prop: 'DateDue',
        dataType: 'date',
      });
      taskColumns.push({
        name: 'Date Completed',
        prop: 'DateCompleted',
        dataType: 'date',
      });
      taskColumns.push({ name: 'Task Type', prop: 'TaskName' });
      taskColumns.push({ name: 'Task Status', prop: 'State' });
      taskColumns.push({
        name: 'Task Summary',
        prop: 'TaskSummary',
        width: 160,
      });
      taskColumns.push({
        name: 'Subject Summary',
        prop: 'SubjectSummary',
        width: 160,
      });
      taskColumns.push({
        name: 'Organisation Name',
        prop: 'OrganisationName',
        width: 160,
      });
      taskColumns.push({
        name: 'Action For',
        prop: 'ActionForUsername',
        width: 160,
      });
      taskColumns.push({
        name: 'Created By',
        prop: 'ActionedByUsername',
        width: 160,
      });

      if (includeArchived) {
        taskColumns.push({
          name: 'Archived',
          prop: 'Archived',
          dataType: 'boolean',
        });
      }
      if (isReferral) {
        taskColumns.push({
          name: 'Is Referral',
          prop: 'IsReferral',
          dataType: 'boolean',
        });
      }
    }

    this.dataGrid.instance.beginUpdate();
    this.columns = [...taskColumns];
    // this.columns=this.columns.filter(c=> data.hasOwnProperty(c.prop));
    this.columns.map((x, idx) => (x.id = idx));
    this.dataGrid.instance.endUpdate();
  }

  setColumns(includeArchived, includeDateIncurred, isReferral, data: any) {
    const subjectType = this.subjectType || this.reportService.subjectType;
    this.hasValue = false;

    const columns: any[] = [];
    const fields = [];
    subjectType.dataFields.forEach((x) => {
      switch (x.name.toLowerCase()) {
        case 'reference':
          fields.push({ id: 1, name: x.label, prop: x.name });
          break;
        case 'summary':
          fields.push({ id: 2, name: x.label, prop: x.name });
          break;
        case 'categoryid':
          fields.push({ id: 3, name: x.label, prop: x.name });
          break;
        default:
          break;
      }
    });
    fields
      .sort((a, b) => a.id - b.id)
      .forEach((x) => {
        columns.push({ name: x.name, prop: x.prop });
      });

    // Add columns relevant to grouping and value
    if (this.report && this.report.config) {
      const dataField = this.getDataFieldById(this.report.config.groupBy);
      if (dataField && dataField.dataType.toLowerCase() !== 'category') {
        columns.push({ name: dataField.label, prop: 'GroupByValue' });
      }
      if (this.report.config.valueLabel > '') {
        const valueDataField = this.getDataFieldById(
          this.report.config.valueId
        );
        if (valueDataField) {
          columns.push({
            name: this.report.config.valueLabel,
            prop: 'AggregateValue',
            dataType: valueDataField.dataType,
            showSummary: true,
          });
        } else {
          columns.push({
            name: this.report.config.valueLabel,
            prop: 'AggregateValue',
            showSummary: true,
          });
        }
        this.hasValue = true;
      }
    }

    const returnType =
      this.report.config.returnType || enumGroupReportReturnType.Subject;

    if (
      returnType === enumGroupReportReturnType.Subject &&
      includeDateIncurred &&
      this.processType
    ) {
      const f = this.processType.dataFields.find(
        (i) => i.name.toLowerCase() === 'dateincurred'
      );
      if (f) {
        columns.push({
          name: f.label,
          prop: f.name,
          dataType: f.dataType.toLowerCase(),
        });
      }
    }

    if (
      returnType === enumGroupReportReturnType.Subject ||
      returnType === enumGroupReportReturnType.SubjectAndProcess
    ) {
      subjectType.dataFields.forEach((x) => {
        if (
          x.id !== this.report.config.valueId &&
          x.id !== this.report.config.groupBy
        ) {
          switch (x.name.toLowerCase()) {
            case 'reference':
            case 'labels':
            case 'categoryid':
            case 'summary':
            case 'primarylink':
            case 'markers':
              // do nothing
              break;
            case 'organisationid':
              columns.push({ name: x.label, prop: x.label });
              console.log(x.label);
              break;
            default:
              // if date, markers or boolean uses a custom cell template...
              if (
                x.dataType.toLowerCase() === 'date' ||
                x.dataType.toLowerCase() === 'boolean' ||
                x.dataType.toLowerCase() === 'currency'
              ) {
                columns.push({
                  name: x.label,
                  prop: x.name,
                  dataType: x.dataType.toLowerCase(),
                });
              } else {
                // not date or boolean use default cell template for display
                columns.push({ name: x.label, prop: x.name });
              }
              break;
          }
        }
      });

      if (includeArchived) {
        columns.push({
          name: 'Archived',
          prop: 'Archived',
          dataType: 'boolean',
        });
      }

      if (isReferral) {
        columns.push({
          name: 'Is Referral',
          prop: 'IsReferral',
          dataType: 'boolean',
        });
      }

      columns.push({
        name: 'Primary Link(s)',
        prop: 'PrimaryLink',
        width: 600,
      });
    }
    if (
      this.report.config.processTypeId &&
      (returnType === enumGroupReportReturnType.Process ||
        returnType === enumGroupReportReturnType.SubjectAndProcess)
    ) {
      this.processType.dataFields.forEach((x) => {
        if (
          x.id !== this.report.config.valueId &&
          x.id !== this.report.config.groupBy
        ) {
          switch (x.name.toLowerCase()) {
            case 'summary':
            case 'linkedsubject':
              // do nothing
              break;
            default:
              // if date, markers or boolean uses a custom cell template...
              if (
                x.dataType.toLowerCase() === 'date' ||
                x.dataType.toLowerCase() === 'boolean' ||
                x.dataType.toLowerCase() === 'currency'
              ) {
                columns.push({
                  name: x.label,
                  prop: x.name,
                  dataType: x.dataType.toLowerCase(),
                });
              } else {
                // not date or boolean use default cell template for display
                columns.push({ name: x.label, prop: x.name });
              }
          }
        }
      });

      if (includeArchived) {
        columns.push({
          name: 'Archived',
          prop: 'Archived',
          dataType: 'boolean',
        });
      }
      columns.push({
        name: 'Primary Link(s)',
        prop: 'PrimaryLink',
        width: 300,
      });
      columns.push({
        name: 'Case Owner',
        prop: 'UserName',
        width: 160,
      });
    }

    // now we have our columns double check/remove any duplicate columns...
    const seen = new Set();
    const uniqueColumns = columns.filter((item) => {
      const duplicate = seen.has(item.name);
      seen.add(item.name);
      return !duplicate;
    });

    this.dataGrid.instance.beginUpdate();
    this.columns = [...uniqueColumns];
    this.columns = this.columns.filter((c) => data.hasOwnProperty(c.prop));
    this.columns.map((x, idx) => (x.id = idx));
    this.dataGrid.instance.endUpdate();
  }

  viewReport(viewMode: string) {
    const s = viewMode.toLowerCase();
    this.showChart = s.indexOf('chart') > -1;
    this.showData = s.indexOf('data') > -1;
  }

  // general layout stuff
  printReport() {
    const html = this.content.nativeElement.innerHTML;
    const css =
      '<link rel="stylesheet" type="text/css" href="https://cdn3.devexpress.com/jslib/20.1.4/css/dx.common.css" async /> ' +
      '<link rel="stylesheet" type="text/css" href="https://cdn3.devexpress.com/jslib/20.1.4/css/dx.light.css" async />';
    this.printService.printHtml(this.report.name, html, css, true);
  }

  setChartType(chartType: string) {
    this.chartType = chartType;
    this.pieType = chartType;
    this.doughnut = chartType === 'Doughnut';
    if (this.doughnut) {
      this.chartType = 'Pie';
    }
  }

  toggleFilterRow() {
    // this.dataGrid.toggleFilterRow(); // PJT - This clears any filter already applied which is causing issues when users save and load reports.
    // changed to this code so filter does not get reset.
    this.dataGrid.setFilterVisibility(!this.dataGrid.showFilterRow);
  }

  filterVisibilityChanged(visibility) {
    this.showFilter = visibility;
  }

  reset() {
    this.dataGrid.reset();
  }

  export() {
    this.dataGrid.export(`${this.report.name.replace(/\s/g, '')}.xlsx`);
  }

  save(saveAsFlag: boolean = false) {
    this.report.config.showChart = this.showChart;
    this.report.config.showData = this.showData;
    this.report.config.chartType =
      this.chartType === 'Pie' && this.doughnut ? 'Doughnut' : this.chartType;
    if (this.dataGrid && this.dataGrid.instance) {
      const state = this.dataGrid.instance.state();
      if (state && state.filterPanel) {
        // make sure filterEnabled correctly saved as something is screwing this up if left to DevX
        state.filterPanel.filterEnabled = this.showFilter;
      }
      this.report.config.state = JSON.stringify(state);
    }

    // After dialog is closed, reload reports list data.
    this.dialog.afterAllClosed.subscribe((_) => {
      this.reportService.reset();
    });

    if (this.pieChart) this.pieChart.instance.hideTooltip();
    if (this.barChart) this.barChart.instance.hideTooltip();

    var dlg = this.dialog.open(ReportDialogComponent, {
      data: {
        report: this.report,
        saveAsFlag: saveAsFlag,
      },
    });
  }

  saveAs() {
    // This forces save function to perform save as.
    this.save(true);
  }

  getDataFieldById(id) {
    const subjectType = this.subjectType || this.reportService.subjectType;
    const processType = this.processType;
    let dataField;
    if (id) {
      if (subjectType) {
        dataField = subjectType.dataFields.find((i) => i.id === id);
      }
      if (processType && !dataField) {
        dataField = processType.dataFields.find((i) => i.id === id);
      }
    }
    return dataField;
  }
}

// date picker
// tslint:disable-next-line:max-classes-per-file
// @Component({
//   selector: 'report-date-picker',
//   template: `<div fxLayout="column">
//     <mat-form-field>
//       <input matInput [matDatepicker]="picker" placeholder="From Date" />
//       <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
//       <mat-datepicker #picker></mat-datepicker> </mat-form-field
//     ><mat-form-field>
//       <input matInput [matDatepicker]="picker2" placeholder="To Date" />
//       <mat-datepicker-toggle matSuffix [for]="picker2"></mat-datepicker-toggle>
//       <mat-datepicker #picker2></mat-datepicker>
//     </mat-form-field>
//   </div>`,
// })
// export class ReportDatePickerComponent {
//   constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
// }
